import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Button, Flex, Typography, Spin } from "antd"
import { Editor, EditorProvider, Toolbar, BtnBold, BtnItalic, BtnUnderline, BtnBulletList, BtnNumberedList, BtnUndo, BtnRedo } from "react-simple-wysiwyg"
import { useTranslation } from "react-i18next"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import { AiEnhanceButton } from "ui/AiEnhanceButton"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { ConfirmModal } from "ui/ConfirmModal"
import styles from "./index.module.css"

const { Title } = Typography

const RichTextEditor = ({ value, onChange }) => (
  <EditorProvider>
    <Editor className={styles.templateEditor} value={value} onChange={onChange}>
      <Toolbar>
        <BtnUndo />
        <BtnRedo />
        <BtnBold />
        <BtnItalic />
        <BtnUnderline />
        <BtnBulletList />
        <BtnNumberedList />
      </Toolbar>
    </Editor>
  </EditorProvider>
)

RichTextEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
}

const toEditorHtml = (val) => (val || "").replaceAll("\n", "<br>")

const stripHtml = (html) => {
  const div = document.createElement("div")
  div.innerHTML = html
  return div.textContent?.replaceAll(" ", "").trim() || ""
}

const canonicalizeHtml = (html) => {
  const div = document.createElement("div")
  div.innerHTML = (html || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n").replace(/<[^>]+>/g, "")
  return div.textContent.split("\n").map((l) => l.trim()).filter(Boolean).join("\n")
}

const EditTemplateModal = ({ item, content, open, onClose, onSave, loading, saving }) => {
  const { t } = useTranslation()
  const [messageBody, setMessageBody] = useState("")
  const [footer, setFooter] = useState("")
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [showAiModal, setShowAiModal] = useState(false)
  const [profileDescription, setProfileDescription] = useState("")
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [isResetToDefault, setIsResetToDefault] = useState(false)
  const initialRef = useRef({ messageBody: "", footer: "" })

  useEffect(() => {
    if (!open || !item) return
    if (loading) return
    const initial = {
      messageBody: toEditorHtml(content?.message_body ?? item.message_body ?? ""),
      footer: toEditorHtml(content?.footer ?? item.footer ?? "")
    }
    initialRef.current = initial
    setMessageBody(initial.messageBody)
    setFooter(initial.footer)
  }, [open, item, content, loading])

  const isTouched = () =>
    messageBody !== initialRef.current.messageBody ||
    footer !== initialRef.current.footer

  const isAlreadyDefault = () =>
    canonicalizeHtml(messageBody) === canonicalizeHtml(toEditorHtml(content?.default_message ?? "")) &&
    canonicalizeHtml(footer) === canonicalizeHtml(toEditorHtml(content?.default_footer ?? ""))

  const requestClose = () => {
    if (isTouched()) {
      setShowUnsavedModal(true)
    } else {
      executeClose()
    }
  }

  const executeClose = () => {
    setMessageBody("")
    setFooter("")
    setShowUnsavedModal(false)
    onClose()
  }

  const handleFinish = () => {
    onSave(item.id, messageBody, footer, isResetToDefault)
  }

  const handleReset = () => {
    setShowResetConfirm(false)
    setIsResetToDefault(true)
    setMessageBody(toEditorHtml(content?.default_message ?? ""))
    setFooter(toEditorHtml(content?.default_footer ?? ""))
  }

  const handleTokenClick = (token) => {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      const container = range.startContainer
      const editorEl = container.nodeType === Node.TEXT_NODE ? container.parentElement : container
      if (editorEl?.closest?.("[contenteditable]")) {
        editorEl.closest("[contenteditable]").focus()
        document.execCommand("insertText", false, token + " ")
        return
      }
    }
    setMessageBody((prev) => prev + token)
  }

  const tokens = (content?.available_tokens ?? []).map((token) => ({
    label: token.label,
    token: `[${token.name}]`
  }))

  return (
    <>
      <Modal
        open={open}
        onCancel={requestClose}
        footer={null}
        width={700}
        maskClosable={false}
        centered
        destroyOnClose
      >
        <Title level={4} className="mb-m">
          {item?.name || t("communication_module.edit_template.title")}
        </Title>

        {loading ? (
          <Flex justify="center" align="center" className={styles.spinContainer}>
            <Spin />
          </Flex>
        ) : (
          <Form layout="vertical" onFinish={handleFinish}>
            {tokens.length > 0 && (
              <Form.Item label={t("communication_module.edit_template.tokens_label")}>
                <Flex gap={8} wrap="wrap">
                  {tokens.map((token) => (
                    <button
                      key={token.token}
                      type="button"
                      className={styles.tokenChip}
                      onClick={() => handleTokenClick(token.token)}
                    >
                      {token.label}
                    </button>
                  ))}
                </Flex>
              </Form.Item>
            )}

            <Form.Item label={t("communication_module.edit_template.body_label")}>
              <Flex justify="flex-end" className="mb-s">
                <AiEnhanceButton
                  text={stripHtml(messageBody)}
                  onClick={() => setShowAiModal(true)}
                />
              </Flex>
              <RichTextEditor value={messageBody} onChange={(e) => { setMessageBody(e.target.value); setIsResetToDefault(false) }} />
            </Form.Item>

            <Form.Item label={t("communication_module.edit_template.footer_label")}>
              <RichTextEditor value={footer} onChange={(e) => { setFooter(e.target.value); setIsResetToDefault(false) }} />
            </Form.Item>

            <Flex justify="space-between" className="mt-m">
              <Button danger onClick={() => setShowResetConfirm(true)} disabled={saving || isAlreadyDefault()}>
                {t("communication_module.edit_template.reset_to_default")}
              </Button>
              <Flex gap={8}>
                <Button onClick={requestClose}>
                  {t("communication_module.edit_template.cancel")}
                </Button>
                <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>
                  {t("communication_module.edit_template.save")}
                </Button>
              </Flex>
            </Flex>
          </Form>
        )}
      </Modal>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onKeepEditing={() => setShowUnsavedModal(false)}
        onDiscard={executeClose}
      />

      <ConfirmModal
        open={showResetConfirm}
        title={t("communication_module.edit_template.reset_confirm_title")}
        info={t("communication_module.edit_template.reset_confirm_info")}
        okLabel={t("communication_module.edit_template.reset_confirm_ok")}
        cancelLabel={t("communication_module.edit_template.reset_confirm_cancel")}
        onOk={handleReset}
        onCancel={() => setShowResetConfirm(false)}
      />

      {showAiModal && (
        <AiEnhancementModal
          open={showAiModal}
          description={messageBody}
          setDescription={setMessageBody}
          profileDescription={profileDescription}
          setProfileDescription={setProfileDescription}
          modalTitle={t("pro.ai_enhancement.modal_title")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          onOk={() => setShowAiModal(false)}
          onCancel={() => {
            setShowAiModal(false)
            setProfileDescription("")
          }}
        />
      )}
    </>
  )
}

EditTemplateModal.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    message_body: PropTypes.string,
    footer: PropTypes.string
  }),
  content: PropTypes.shape({
    message_body: PropTypes.string,
    footer: PropTypes.string,
    default_message: PropTypes.string,
    default_footer: PropTypes.string,
    available_tokens: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string
    })),
    is_default: PropTypes.bool
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  saving: PropTypes.bool
}

export default EditTemplateModal
